import { LocalLineItems } from "./types";
import { Dispatch, SetStateAction, useState } from "react";
import { formatNumber, preciseRound } from "@/lib/utils";
import TrashIcon from "~icons/fa6-regular/trash-can";
import OutdatedWarning from "./OutdatedWarning";
import PartThumbnail from "@/lib/PartThumbnail";
import { UpdateLineItemsParams } from "./Quote";

export default function LineItemList({
  lineItems,
  selectedLineItemKeys,
  setSelectedLineItemKeys,
  deleteLineItem,
  updateLineItems,
  disabled = false,
  clearLocalLineItems,
}: {
  lineItems: LocalLineItems[];
  selectedLineItemKeys: string[];
  setSelectedLineItemKeys: Dispatch<SetStateAction<string[]>>;
  deleteLineItem: (lineItemKey: string) => void;
  updateLineItems: (params: UpdateLineItemsParams) => void;
  disabled?: boolean;
  clearLocalLineItems: () => void;
}) {
  const totalPrice = getTotalPrice(lineItems);
  const shouldShowTotalPrice = shouldShowTotal(lineItems);
  const [showUnitPriceInputKey, setShowUnitPriceInput] = useState<string>();
  const [showTotalPriceInputKey, setShowTotalPriceInput] = useState<string>();

  return (
    <table className="w-full bg-white border-collapse">
      <thead className="sticky top-0 font-bold text-sm bg-white shadow-xs z-10">
        <tr>
          <th className="text-left p-2">Items</th>
          {!disabled && <th className="text-left"></th>}
          <th className="text-left p-2">Quantity</th>
          <th className="text-left p-2">Lead Time</th>
          <th className="text-left p-2">Unit Price</th>
          <th className="text-left p-2">Total Price</th>
          <th className="p-2"></th>
        </tr>
      </thead>
      <tbody className="text-sm">
        {lineItems?.map(
          ({
            part_number,
            revision_number,
            item_lines,
            description,
            thumbnail_url,
            has_outdated_line_items,
            requested_part_revision_id,
          }) =>
            item_lines.map(
              ({ key, quantity, lead_time, unitPrice, highlight }, i) => (
                <tr
                  key={key}
                  className={`not-last:border-y ${highlight && "bg-blue-50 animate-pulse"}`}
                >
                  {i === 0 && (
                    <td
                      rowSpan={item_lines.length}
                      className="text-left px-6 border-r relative"
                      style={{
                        paddingTop:
                          has_outdated_line_items && item_lines.length < 3
                            ? "2.5rem"
                            : "",
                      }}
                    >
                      {has_outdated_line_items && (
                        <div className="absolute top-1 left-1 w-[calc(100%-0.5rem)]">
                          <OutdatedWarning
                            partId={requested_part_revision_id}
                            clearLocalLineItems={clearLocalLineItems}
                          />
                        </div>
                      )}
                      <div className="flex gap-4 items-center">
                        {thumbnail_url && (
                          <PartThumbnail
                            className="size-10"
                            url={thumbnail_url}
                          />
                        )}
                        <div className="flex flex-col gap-1">
                          <div className="text-base">
                            {part_number}{" "}
                            <span className="text-slate-500">Rev</span>{" "}
                            {revision_number}
                          </div>
                          {description && (
                            <div className="text-slate-500">{description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                  )}
                  {!disabled && (
                    <td className="text-left p-4">
                      <input
                        type="checkbox"
                        checked={selectedLineItemKeys.includes(key)}
                        onChange={() => handleToggleLineItemCheckBox(key)}
                      />
                    </td>
                  )}
                  <td className="text-left px-2 py-4">
                    {formatNumber(quantity)}
                  </td>
                  <td className="text-left px-2">{lead_time}</td>
                  <td className="text-left px-2">
                    <span
                      onClick={() => setShowUnitPriceInput(key)}
                      className="mr-0.5"
                    >
                      $
                    </span>
                    {showUnitPriceInputKey === key ? (
                      <input
                        type="number"
                        value={unitPrice}
                        autoFocus
                        className="min-w-13 field-sizing-content px-0.5"
                        onChange={(e) => {
                          updateLineItems({
                            lineItems: updateUnitPrice(
                              lineItems,
                              key,
                              e.target.value,
                            ),
                            save: false,
                          });
                        }}
                        onBlur={() => {
                          setShowUnitPriceInput(undefined);
                          updateLineItems({ lineItems, save: true });
                        }}
                      />
                    ) : (
                      <span onClick={() => setShowUnitPriceInput(key)}>
                        {Number.isNaN(unitPrice)
                          ? ""
                          : formatNumber(unitPrice, true)}
                      </span>
                    )}
                  </td>
                  <td className="text-left px-2">
                    <span
                      onClick={() => setShowTotalPriceInput(key)}
                      className="mr-0.5"
                    >
                      $
                    </span>
                    {showTotalPriceInputKey === key ? (
                      <input
                        type="number"
                        value={preciseRound(quantity * unitPrice, 2)}
                        autoFocus
                        className="min-w-13 field-sizing-content px-0.5"
                        onChange={(e) => {
                          const numberValue = parseFloat(e.target.value);
                          if (numberValue < 0) return;

                          const newUnitPrice = numberValue / quantity;
                          updateLineItems({
                            lineItems: updateUnitPrice(
                              lineItems,
                              key,
                              newUnitPrice.toString(),
                            ),
                            save: false,
                          });
                        }}
                        onBlur={() => {
                          setShowTotalPriceInput(undefined);
                          updateLineItems({ lineItems, save: true });
                        }}
                      />
                    ) : (
                      <span onClick={() => setShowTotalPriceInput(key)}>
                        {Number.isNaN(unitPrice)
                          ? ""
                          : formatNumber(quantity * unitPrice, true)}
                      </span>
                    )}
                  </td>
                  {!disabled && (
                    <td>
                      <TrashIcon
                        className="cursor-pointer text-slate-400 hover:text-slate-600 size-3"
                        onClick={() => deleteLineItem(key)}
                      />
                    </td>
                  )}
                </tr>
              ),
            ),
        )}
        {shouldShowTotalPrice && (
          <tr>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td className="text-right p-2">Total:</td>
            <td className="text-left p-2">${formatNumber(totalPrice, true)}</td>
            <td></td>
          </tr>
        )}
      </tbody>
    </table>
  );

  function handleToggleLineItemCheckBox(lineItemKey: string) {
    if (selectedLineItemKeys.includes(lineItemKey)) {
      setSelectedLineItemKeys(
        selectedLineItemKeys.filter((k) => k !== lineItemKey),
      );
    } else {
      setSelectedLineItemKeys([...selectedLineItemKeys, lineItemKey]);
    }
  }
}

function getTotalPrice(lineItems: LocalLineItems[]): number {
  return lineItems.reduce((sum, part) => sum + getPartTotalPrice(part), 0);
}

function getPartTotalPrice(part: LocalLineItems): number {
  return part.item_lines.reduce((sum, { unitPrice, quantity }) => {
    const isValidPrice = !isNaN(unitPrice) && unitPrice >= 0;
    if (!isValidPrice) {
      return sum;
    }
    return sum + unitPrice * quantity;
  }, 0);
}

function updateUnitPrice(
  parts: LocalLineItems[],
  lineItemKey: string,
  value: string,
) {
  return parts.map((p) => ({
    ...p,
    item_lines: p.item_lines.map((l) => ({
      ...l,
      unitPrice: l.key === lineItemKey ? parseFloat(value) : l.unitPrice,
    })),
  }));
}

function shouldShowTotal(lineItems: LocalLineItems[]): boolean {
  // Only show total price if all parts have exactly one item line
  return (
    lineItems.length > 0 &&
    lineItems.every((part) => part.item_lines.length === 1)
  );
}
